import { motion } from "framer-motion";
import { Award, Building2, Scale, Users } from "lucide-react";

const CREDENTIALS = [
  { icon: Scale, label: '35+ Years', sub: 'In land development' },
  { icon: Award, label: '$350M', sub: 'Equity transactions' },
  { icon: Building2, label: '2,200+ ac', sub: 'In specific plans' },
  { icon: Users, label: 'NMC', sub: 'Executive Management' },
];

const ABOUT_IMG = "https://images.pexels.com/photos/8961260/pexels-photo-8961260.jpeg";

export const About = () => {
  return (
    <section id="about" className="py-24 lg:py-40 bg-paper relative">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left index column */}
          <div className="lg:col-span-3">
            <div className="sticky top-28">
              <span className="mono text-[10px] uppercase tracking-[0.25em] text-clay">Section · 01</span>
              <h2 className="font-display text-3xl lg:text-4xl text-ink mt-3 leading-[1.05]">
                Working <br /><em className="font-light">with</em> RCCD
              </h2>
              <p className="mt-4 text-sm text-muted-foreground">A land development partner in Southern California — with a current focus on entitlement.</p>
            </div>
          </div>

          {/* Center copy — sourced from rccdinc.com/who-we-are */}
          <div className="lg:col-span-5">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className="font-display text-3xl lg:text-5xl leading-[1.08] text-ink text-balance tracking-[-0.02em]"
            >
              Over <em className="text-clay font-light">35 years</em> in land development, large-scale real estate, and homebuilding — with a current focus on land entitlement.
            </motion.h3>

            <div className="mt-10 space-y-5 text-base lg:text-lg text-foreground/80 leading-relaxed">
              <p>
                Recently, RCCD completed over <strong className="text-ink">$350 million</strong> in equity transactions with $50 million in pending transactions. We also provide ongoing Executive Management Services in support of the <strong className="text-ink">New Model Colony</strong> consortium of builders and developers.
              </p>
              <p>
                The NMC is an exclusive group developing <strong className="text-ink">4,000 acres in the City of Ontario</strong>. When complete, it will house over 30,000 residents along with schools, shopping centers, a college, office park, and industrial areas. Public-record achievements include the award-winning Mill Creek Regional Wetlands, tens of miles of storm drains, sewer lines and regional streets, a bridge, a fire station, and a new elementary school as part of an 8-school mitigation agreement.
              </p>
              <p>
                In addition to Ontario, we work in the City of Chino and other locations across Riverside, San Bernardino, Los Angeles, and Orange Counties — wherever Southern California land needs an entitlement partner. RCCD has authored, co-authored, or amended <strong className="text-ink">5 specific plans</strong> in Ontario and Chino totalling more than 2,200 acres. Two additional specific plans for 125 acres are currently in process, with a pipeline of four further projects.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-6 text-sm">
              {[
                { k: '35+', v: 'Years of experience' },
                { k: '$350M', v: 'Equity transactions' },
                { k: '5', v: 'Specific plans authored' },
                { k: '4,000', v: 'NMC acres' },
              ].map(s => (
                <div key={s.k} className="flex items-baseline gap-2 border-l border-border pl-4">
                  <span className="font-display text-3xl text-clay">{s.k}</span>
                  <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{s.v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right visual + credentials */}
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9 }}
              className="relative aspect-[3/4] overflow-hidden bg-secondary"
            >
              <video
                src="https://customer-assets.emergentagent.com/job_interactive-showcase-79/artifacts/z5ufklc6_construction-workers-operate-bulldozers-while-building-new-homes-and-road-top-drone-SBV-347244765-HD.mp4"
                poster={ABOUT_IMG}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-paper/70">New Model Colony · Ontario</div>
                <div className="font-display text-paper text-2xl mt-1">A 4,000-acre community in the making</div>
              </div>
            </motion.div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {CREDENTIALS.map(c => (
                <div key={c.label} className="group border border-border bg-card p-4 hover:border-clay transition-colors">
                  <c.icon className="h-5 w-5 text-clay group-hover:scale-110 transition-transform" />
                  <div className="font-display text-base text-ink mt-3 leading-tight">{c.label}</div>
                  <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{c.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
